import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { filter, isEmpty, capitalize } from 'lodash';
import moment from 'moment';
import { gravatarUrl, humanizeTenantId } from '@a0/support-center-common/lib/tenants';
import HotjarMasked from '../HotjarMasked';
import Loading from '../Loading';
import Error from '../Error';
import SelectInput from '../SelectInput';
import Clipboard from '../Clipboard';
import TextInput from '../TextInput';
import { isEquivalent } from '../../utils/arrayUtility';

import './module.css';

class InvitationList extends Component {
  static isInvitationExpired(expiresAt) {
    return moment(expiresAt).isBefore(moment());
  }

  static renderStatus(status) {
    const humanized = capitalize(status);
    if (status === 'accepted')
      return <span className="label label-info">{humanized}</span>;

    if (status === 'pending')
      return <span className="label label-warning">{humanized}</span>;

    return '...';
  }

  constructor(props, context) {
    super(props, context);
    this.state = {
      tenantFilter: '',
      emailFilter: '',
      statusFilter: '',
      filteredInvitations: this.props.invitations
    };

    this.setFilter = this.setFilter.bind(this);
    this.filterInvitations = this.filterInvitations.bind(this);
  }

  componentDidUpdate({ invitations: prevInvitations }) {
    const { invitations: currentInvitations } = this.props;
    if (!isEquivalent(currentInvitations, prevInvitations)) {
      this.filterInvitations(this.state);
    }
  }

  setFilter(key) {
    return e => {
      const state = {};
      state[key] = e.target.value.toLowerCase();
      this.filterInvitations(state);
    };
  }

  filterInvitations(state) {
    const filteredInvitations = filter(this.props.invitations, i => {
      if (
        state.tenantFilter &&
        state.tenantFilter !== '' &&
        !i.tenants.includes(state.tenantFilter)
      ) {
        return false;
      }
      if (
        state.emailFilter &&
        state.emailFilter !== '' &&
        !i.recipientEmail.includes(state.emailFilter)
      ) {
        return false;
      }
      if (
        state.statusFilter &&
        state.statusFilter !== '' &&
        i.status !== state.statusFilter
      ) {
        return false;
      }
      return true;
    });

    this.setState({ ...state, filteredInvitations });
  }

  renderRow({
    _id,
    recipientEmail,
    acceptanceEmail,
    token,
    tenants,
    status,
    expiresAt,
    createdAt
  }) {
    const { withRemove, invitationRemover, withExpires, withUrl } = this.props;
    const safeEmail = recipientEmail ? recipientEmail.replace(/\W/g, '') : '';
    const avatar = gravatarUrl(recipientEmail, safeEmail);

    return (
      <tr key={_id}>
        <td styleName="first">
          <img styleName="avatar" className="avatar" src={avatar} alt="" />
        </td>
        <td colSpan="2">
          <HotjarMasked isInline>
            {recipientEmail}
            {acceptanceEmail && recipientEmail !== acceptanceEmail ? (
              ` (${acceptanceEmail})`
            ) : null}
          </HotjarMasked>
        </td>
        <td colSpan="2">
          <ul>
            {tenants &&
              Array.isArray(tenants) &&
              tenants.map(tenant => {
                return (
                  <li key={tenant}>
                    {withRemove &&
                    invitationRemover && (
                      <span>
                        [<a
                          href="#!"
                          onClick={invitationRemover(_id, [tenant])}
                        >
                          ⨉
                        </a>]
                      </span>
                    )}{' '}
                    <HotjarMasked isInline>
                      {humanizeTenantId(tenant)}
                    </HotjarMasked>
                  </li>
                );
              })}
          </ul>
        </td>
        <td className="visible-lg">
          {status && InvitationList.renderStatus(status)}
        </td>
        <td className="visible-lg">
          {moment(createdAt)
            .fromNow()
            .replace('a few seconds ago', 'just now')}
        </td>
        {withExpires && (
          <td className="visible-lg">
            {moment(expiresAt)
              .fromNow()
              .replace('a few seconds ago', 'just now')}
          </td>
        )}
        {withUrl && (
          <td colSpan="2">
            {!InvitationList.isInvitationExpired(expiresAt) && (
              <Clipboard
                text={`https://support.auth0.com/register?token=${token}`}
              />
            )}
          </td>
        )}
        {withRemove &&
        invitationRemover && (
          <td>
            <button
              className="btn btn-default btn-sm"
              onClick={invitationRemover(_id, tenants)}
            >
              <span className="btn-icon icon-budicon-263" />
              Remove
            </button>
          </td>
        )}
      </tr>
    );
  }

  renderTable() {
    const { filteredInvitations } = this.state;
    const { withExpires, withUrl, withRemove, invitationRemover } = this.props;
    return (
      <table styleName="invitation-list" className="table table-fixed">
        <thead>
          <tr>
            <td styleName="first" />
            <td colSpan="2">Email</td>
            <td colSpan="2" style={{ width: '19%' }}>
              Tenants
            </td>
            <td className="visible-lg">Status</td>
            <td className="visible-lg">Invited</td>
            {withExpires && <td className="visible-lg">Expires</td>}
            {withUrl && <td colSpan="2" />}
            {withRemove && invitationRemover && <td>Actions</td>}
          </tr>
        </thead>
        <tbody>
          {filteredInvitations
            .filter(invitation => invitation && invitation.recipientEmail)
            .map(invitation => this.renderRow(invitation))}
        </tbody>
      </table>
    );
  }

  renderFilters() {
    const { tenants } = this.props;
    return (
      <div className="row">
        <div className="col-xs-12 col-sm-4">
          <TextInput
            name="email"
            label="Filter by email:"
            onChange={this.setFilter('emailFilter')}
            value={this.state.emailFilter}
          />
        </div>
        {!isEmpty(tenants) && (
          <div className="col-xs-12 col-sm-4">
            <SelectInput
              name="tenantFilter"
              label="Filter by tenant:"
              defaultOption="All Tenants"
              options={tenants}
              onChange={this.setFilter('tenantFilter')}
              value={this.state.tenantFilter}
              hotjarMasked
            />
          </div>
        )}
        <div className="col-xs-12 col-sm-4">
          <SelectInput
            name="status"
            label="Filter by status:"
            defaultOption="All statuses"
            options={[
              {
                value: 'pending',
                text: 'Pending'
              },
              {
                value: 'accepted',
                text: 'Accepted'
              }
            ]}
            onChange={this.setFilter('statusFilter')}
            value={this.state.statusFilter}
          />
        </div>
      </div>
    );
  }

  render() {
    const { loading, success, error } = this.props;
    const { filteredInvitations } = this.state;
    return (
      <Loading show={loading}>
        <Error message={error}>
          <div>
            {this.renderFilters()}
            {success && filteredInvitations.length ? (
              this.renderTable()
            ) : (
              <p styleName="invitation-list center">
                No user invitations were found matching the current filter
                criteria.
              </p>
            )}
          </div>
        </Error>
      </Loading>
    );
  }
}

InvitationList.propTypes = {
  loading: PropTypes.bool,
  success: PropTypes.bool,
  error: PropTypes.string,
  tenants: PropTypes.array,
  withUrl: PropTypes.bool,
  withExpires: PropTypes.bool,
  withRemove: PropTypes.bool,
  invitationRemover: PropTypes.func,
  invitations: PropTypes.arrayOf(
    PropTypes.shape({
      recipientEmail: PropTypes.string,
      senderId: PropTypes.string,
      acceptanceEmail: PropTypes.string,
      tenants: PropTypes.array,
      status: PropTypes.string,
      createdAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      expiresAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  )
};

export default InvitationList;
